/**
 * /projects/new/chat — AI chat intake (M4.2).
 *
 * Thin route wrapper around the shared <ChatIntakePanel> (page variant). The
 * panel owns the conversation, draft build/review, autosave, and create; this
 * wrapper only translates the PDF→chat hand-off (router state) into the panel's
 * `pdfHandoff` prop and routes to the created project. The same panel renders
 * embedded in the calendar's New-project modal.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChatIntakePanel } from '@/components/intake/ChatIntakePanel';
import type { PdfChatHandoff } from '@/components/intake/intakePanelShared';

export function ProjectNewChat() {
  const navigate = useNavigate();
  const location = useLocation();
  const [handoff, setHandoff] = useState<PdfChatHandoff | null>(null);
  const seededRef = useRef(false);

  // Pull a PDF→chat hand-off out of the router state once, then clear it so a
  // refresh / back-nav doesn't re-seed the conversation.
  useEffect(() => {
    if (seededRef.current) return;
    const st = location.state as { fromPdf?: PdfChatHandoff } | null;
    if (!st?.fromPdf?.files?.length) return;
    seededRef.current = true;
    setHandoff(st.fromPdf);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location, navigate]);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      <ChatIntakePanel
        variant="page"
        pdfHandoff={handoff}
        onCreated={(p) => navigate(`/projects/${p.id}`)}
      />
    </div>
  );
}
